import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import { useConfig } from './hooks/useConfig';
import './App.css';

// Lazy load components for code splitting
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Schedules = lazy(() => import('./pages/Schedules'));
const Shifts = lazy(() => import('./pages/Shifts'));
const Users = lazy(() => import('./pages/Users'));
const Roles = lazy(() => import('./pages/Roles'));
const TimeOff = lazy(() => import('./pages/TimeOff').then(module => ({ default: module.default })));
const ShiftRequests = lazy(() => import('./pages/ShiftRequests'));
const Reports = lazy(() => import('./pages/Reports'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Profile = lazy(() => import('./pages/Profile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminCustomizationsPage = lazy(() => import('./pages/AdminCustomizationsPage'));
const AdminScheduleGroups = lazy(() => import('./pages/AdminScheduleGroups'));
const TradeBoard = lazy(() => import('./pages/trades/TradeBoard'));
const ManagerReview = lazy(() => import('./pages/trades/ManagerReview'));


// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

// Component to protect trades routes when feature is disabled or role is viewer
function TradesProtectedRoute({ children }: { children: React.ReactNode }) {
  const { config } = useConfig();
  const { user } = useAuth();

  // Wait for config to load (ENABLE_SHIFT_TRADES will be undefined initially)
  // If it's undefined, we haven't loaded the config yet, so show loading
  if (config.ENABLE_SHIFT_TRADES === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const isViewer = user?.roles?.some((r) => String(r).toLowerCase() === 'viewer') ?? false;
  if (isViewer) {
    return <Navigate to="/dashboard" replace />;
  }

  const isTradesEnabled = config.ENABLE_SHIFT_TRADES === 'true';

  if (!isTradesEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const { 
    showSessionTimeoutModal, 
    sessionTimeRemaining, 
    extendSession, 
    forceLogout 
  } = useAuth();

  return (
    <>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute />}>
            <Route index element={<Dashboard />} />
          </Route>
          <Route path="/profile" element={<ProtectedRoute />}>
            <Route index element={<Profile />} />
          </Route>
          <Route path="/users" element={<ProtectedRoute />}>
            <Route index element={<Users />} />
          </Route>
          <Route path="/shifts" element={<ProtectedRoute />}>
            <Route index element={<Shifts />} />
          </Route>
          <Route path="/schedules" element={<ProtectedRoute />}>
            <Route index element={<Schedules />} />
          </Route>
          <Route path="/timeoff" element={<ProtectedRoute />}>
            <Route index element={<TimeOff />} />
          </Route>
          <Route path="/shiftrequests" element={<ProtectedRoute />}>
            <Route index element={<ShiftRequests />} />
          </Route>
          <Route path="/notifications" element={<ProtectedRoute />}>
            <Route index element={<Notifications />} />
          </Route>
          <Route path="/reports" element={<ProtectedRoute />}>
            <Route index element={<Reports />} />
          </Route>
          <Route path="/roles" element={<ProtectedRoute />}>
            <Route index element={<Roles />} />
          </Route>
          <Route path="/admin-customizations" element={<ProtectedRoute />}>
            <Route index element={<AdminCustomizationsPage />} />
          </Route>
          <Route path="/admin-schedule-groups" element={<ProtectedRoute />}>
            <Route index element={<AdminScheduleGroups />} />
          </Route>
          <Route path="/trades" element={<ProtectedRoute />}>
            <Route index element={
              <TradesProtectedRoute>
                <TradeBoard />
              </TradesProtectedRoute>
            } />
          </Route>
          <Route path="/trades/manager" element={<ProtectedRoute />}>
            <Route index element={
              <TradesProtectedRoute>
                <ManagerReview />
              </TradesProtectedRoute>
            } />
          </Route>

          <Route path="/change-password" element={<ChangePassword />} />
        </Routes>
      </Router>
      
      <SessionTimeoutModal
        isVisible={showSessionTimeoutModal}
        onExtendSession={extendSession}
        onSessionExpired={forceLogout}
        timeRemaining={sessionTimeRemaining}
      />
    </>
  );
}

function App() {
  return (
    <NotificationProvider>
      <Suspense fallback={<LoadingSpinner />}>
        <AppContent />
      </Suspense>
    </NotificationProvider>
  );
}

export default App;
